import { Drawing } from "drawing";

function rectSymbol(x, y, name, { stroke = "#0066E5", lineWidth = 2 } = {}) {
  return new Drawing()
    .symbol(
      [
        {
          type: "polyface",
          faces: [
            [
              { x: -4, y: -4 },
              { x: 4, y: -4 },
              { x: 4, y: 4 },
              { x: -4, y: 4 },
            ],
          ],
        },
      ],
      { x, y },
    )
    .style({ stroke, fill: "white", lineWidth })
    .name(name);
}

export default function clipRectangle(
  type,
  clip,
  id,
  { stroke = "#0066E5", lineWidth = 2, inverted = true } = {},
) {
  if (!clip) return null;

  const { x, y, width, height } = clip;
  const style = { stroke, lineWidth };

  const y1 = inverted ? -y : y;
  const y2 = inverted ? -y - height : y + height;

  const outline = new Drawing()
    .polyline([
      { x, y: y1 },
      { x: x + width, y: y1 },
      { x: x + width, y: y2 },
      { x, y: y2 },
      { x, y: y1 },
    ])
    .style({ stroke, fill: "transparent", lineWidth })
    .name(`${type}-clip_${id}_outline`);

  const corners = [
    rectSymbol(x, y2, `${type}-clip_${id}_0`, style),
    rectSymbol(x + width, y2, `${type}-clip_${id}_1`, style),
    rectSymbol(x + width, y1, `${type}-clip_${id}_2`, style),
    rectSymbol(x, y1, `${type}-clip_${id}_3`, style),
  ];

  return new Drawing().add(outline, ...corners);
}

/**
 * @typedef {Object} ClipRectangle
 * @property {number} x - The x-coordinate of the rectangle.
 * @property {number} y - The y-coordinate of the rectangle.
 * @property {number} width - The width of the rectangle.
 * @property {number} height - The height of the rectangle.
 */

/**
 * Updates the clip rectangle by dragging one of its corners to a new position.
 *
 * @param {ClipRectangle} clip - The clip rectangle object.
 * @param {number|string} index - The index (or stringified index) of the corner being dragged (0-3).
 * @param {Object} to - The target point to drag the corner to.
 * @param {number} to.x - The x-coordinate of the target point.
 * @param {number} to.y - The y-coordinate of the target point.
 * @returns {Object} The updated clip rectangle object.
 */
function draggedClip(clip, index, to) {
  const vertices = [
    { x: clip.x, y: clip.y + clip.height },
    { x: clip.x + clip.width, y: clip.y + clip.height },
    { x: clip.x + clip.width, y: clip.y },
    { x: clip.x, y: clip.y },
  ];

  const i = typeof index === "string" ? parseInt(index) : index;

  const opp = vertices[(i + 2) % 4];
  const minX = Math.min(to.x, opp.x);
  const minY = Math.min(to.y, opp.y);
  const maxX = Math.max(to.x, opp.x);
  const maxY = Math.max(to.y, opp.y);

  return {
    ...clip,
    x: minX,
    y: minY,
    width: Math.max(maxX - minX, 10),
    height: Math.max(maxY - minY, 10),
  };
}

function screenScaledClip(clip, viewer, pageIndex) {
  const l0 = viewer.pixelCoords({ x: clip.x, y: clip.y, index: pageIndex });
  const l1 = viewer.pixelCoords({
    x: clip.x + clip.width,
    y: clip.y + clip.height,
    index: pageIndex,
  });
  return {
    x: l0.x,
    y: l0.y,
    width: l1.x - l0.x,
    height: l1.y - l0.y,
  };
}

function constrainClip(clip, minX = 0, minY = 0, maxX = 100, maxY = 100) {
  const x = Math.min(Math.max(clip.x, minX), maxX - clip.width);
  const y = Math.min(Math.max(clip.y, minY), maxY - clip.height);

  return { ...clip, x, y };
}

export { clipRectangle, draggedClip, screenScaledClip, constrainClip };
